(() => {
  if (customElements.get('flooring-card-gallery')) return;

  class FlooringCardGallery extends HTMLElement {
    connectedCallback() {
      this.mobile = window.matchMedia('(max-width: 767px)');
      this.lifecycle = new AbortController();
      this.index = 0;
      this.frame = 0;
      this.onViewportChange = () => {
        if (this.mobile.matches) this.initialize();
      };
      this.mobile.addEventListener('change', this.onViewportChange);
      this.onViewportChange();
    }

    initialize() {
      if (this.track) return;
      const template = this.querySelector('template');
      if (!template) return;
      this.append(template.content.cloneNode(true));
      this.track = this.querySelector('.flooring-card-gallery__track');
      this.slides = [...this.querySelectorAll('.flooring-card-gallery__slide')];
      this.dots = [...this.querySelectorAll('.flooring-card-gallery__dot')];
      const signal = this.lifecycle.signal;

      this.track.addEventListener('scroll', () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          if (this.track.clientWidth) {
            this.update(Math.round(this.track.scrollLeft / this.track.clientWidth));
          }
        });
      }, { passive: true, signal });

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goTo(index), { signal });
      });

      this.track.addEventListener('keydown', (event) => {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        const targets = {
          ArrowLeft: this.index - 1,
          ArrowRight: this.index + 1,
          Home: 0,
          End: this.slides.length - 1
        };
        if (!(event.key in targets)) return;
        event.preventDefault();
        this.goTo(targets[event.key]);
      }, { signal });

      this.track.addEventListener('pointerdown', (event) => {
        this.startX = event.clientX;
        this.startY = event.clientY;
        this.swiped = false;
      }, { passive: true, signal });

      this.track.addEventListener('pointermove', (event) => {
        if (this.startX === undefined) return;
        const horizontal = Math.abs(event.clientX - this.startX);
        const vertical = Math.abs(event.clientY - this.startY);
        if (horizontal > 8 && horizontal > vertical) this.swiped = true;
      }, { passive: true, signal });

      this.track.addEventListener('pointerup', () => {
        this.startX = undefined;
      }, { passive: true, signal });

      this.track.addEventListener('pointercancel', () => {
        this.swiped = true;
        this.startX = undefined;
      }, { passive: true, signal });

      this.track.addEventListener('click', (event) => {
        if (!this.swiped) return;
        event.preventDefault();
        event.stopPropagation();
        this.swiped = false;
      }, { capture: true, signal });

      this.resizeObserver = new ResizeObserver(() => {
        if (!this.track.clientWidth) return;
        this.track.scrollTo({
          left: this.index * this.track.clientWidth,
          behavior: 'instant'
        });
      });
      this.resizeObserver.observe(this.track);
      this.setAttribute('data-ready', '');
      this.update(0);
    }

    goTo(index) {
      const target = Math.max(0, Math.min(index, this.slides.length - 1));
      this.track.scrollTo({
        left: target * this.track.clientWidth,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'instant'
          : 'smooth'
      });
    }

    update(index) {
      this.index = Math.max(0, Math.min(index, this.slides.length - 1));
      this.dots.forEach((dot, i) => {
        dot.setAttribute('aria-current', String(i === this.index));
        this.slides[i].setAttribute('tabindex', i === this.index ? '0' : '-1');
        this.slides[i].setAttribute('aria-hidden', String(i !== this.index));
      });
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.mobile?.removeEventListener('change', this.onViewportChange);
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      if (this.track) {
        this.track.remove();
        this.querySelector('.flooring-card-gallery__dots')?.remove();
        this.track = null;
        this.removeAttribute('data-ready');
      }
    }
  }

  customElements.define('flooring-card-gallery', FlooringCardGallery);
})();